import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import { useTranslation } from "react-i18next";

import AddToCalendarModal from "../../../parts/modals/add-to-calendar";
import SubmitForReviewModal from "../../../parts/modals/submit-for-review";
import EditProgramSettingsModal from "../../../parts/modals/edit-program-settings";
import EditCycleSettingsModal from "../../../parts/modals/edit-cycle";
import ProgramSettingsModal from "../../../parts/modals/program-settings";
import SuggestUpgradeModal from "../../../parts/modals/suggest-upgrade";

import getCycleCardImagePath from "../../../utils/get-cycle-card-image-path";
import Chart from "../../../parts/chart";
import ProgramCard from "../../../parts/cards/program-card";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import generateAvatarSVG from "../../../utils/generate-avatar-svg";
import SvgIcon from "../../../parts/SvgIcon";
import { useRillContext } from "../../../rill/RillReact/share/base";

const {
  STRIPE,
  WEIGHT_UNITS,
  PERIOD_FORMATS,
  DATE_FORMATS: { DATE_PICKER_FORMAT },
} = require("../../../constants");

const handleLike = (e, cycle) => {
  submit(`/actions/cycle/like/${cycle._id}`, { method: "GET" });
};

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("dashboard");

  const {
    editCycle,
    libraryCycles,
    activeProgramIndex,
    activeUser,
    organizationOwner,
    exercisesLiftsForCycle,
    exercisesVolumeForCycle,
    query,
  } = locals;
  const cycle = editCycle
    ? editCycle
    : _.find(libraryCycles, { _id: locals.params.cycleId });

  const { period, units } = query;

  const liftsTotals =
    Object.assign(
      {},
      ..._.map(exercisesLiftsForCycle, (k, i) => {
        return { [i]: _.sum(_.map(k)) };
      })
    ) || 0;

  const totalLiftSum = _.sum(_.map(_.omit(liftsTotals, "assistant")));

  let selectedMetrics =
    query.type === "total"
      ? exercisesLiftsForCycle
      : _.pick(exercisesLiftsForCycle, [query.type]);

  selectedMetrics = _.omit(selectedMetrics, "assistant");

  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();

  const labels = _.map(keys, (i) => `${PERIOD_FORMATS[period]}${i}`);

  const liftSeries = _.map(selectedMetrics, (data, name) => {
    return {
      name,
      className: name,
      data: _.map(keys, (key) => _.get(data, key)),
    };
  });

  const totalVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(liftSeries, `data.${i}`))
  );

  const maxVolume = _.max(totalVolumes);

  // duplicate of the lift totals above to get total volume
  // TODO should try to not have to run this twice...maybe?
  const selectedVolumeMetrics =
    query.type === "total"
      ? exercisesVolumeForCycle
      : _.pick(exercisesVolumeForCycle, [query.type]);

  const volumeSeries = _.map(selectedVolumeMetrics, (data, name) => {
    return {
      name,
      className: name,
      data: _.map(keys, (key) => _.get(data, key)),
    };
  });
  const accumulatedVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(volumeSeries, `data.${i}.volume`))
  );

  const [firstName, lastName] = _.get(cycle, "createdBy.name", "")
    ? _.get(cycle, "createdBy.name", "").split(/ +/)
    : ["", ""];

  const image = _.get(cycle, "image.file", false)
    ? getCycleCardImagePath(cycle)
    : generateAvatarSVG(firstName, lastName);
  const program = cycle.programs[activeProgramIndex];

  const volumeTotalsByLift = Object.assign(
    {},
    ..._.map(exercisesVolumeForCycle, (exercise, i) => {
      return { [i]: _.sumBy(_.map(exercisesVolumeForCycle[i]), "volume") };
    })
  );

  const totalLiftVolume =
    _.get(volumeTotalsByLift, "bench", 0) +
    _.get(volumeTotalsByLift, "squat", 0) +
    _.get(volumeTotalsByLift, "deadlift", 0);

  const intensityTotals = Object.keys(selectedVolumeMetrics).reduce(
    (prev, exercise, it) => {
      let totals = prev;
      exercise = exercisesVolumeForCycle[exercise];

      const averages = _.map(Object.keys(exercise), (w, it) => {
        const week = _.get(exercise, `${w}`);

        if (week.peak > totals.peak) totals.peak = week.peak;

        const average = week.average === null ? 0 : week.average;
        totals.averages.push(average);

        if (!totals.peakSeries[w]) totals.peakSeries[w] = [];

        totals.peakSeries[w].push({ peak: week.peak, week: w });
        totals.averageSeries.push({
          average: average,
          lifts: week.lifts,
          week: w,
        });
      });
      totals.average = (_.sum(totals.averages) / totals.averages.length) * 100;

      return totals;
    },
    { averages: [], peak: 0, average: 0, peakSeries: [], averageSeries: [] }
  );

  const peakSeries = _(_.compact(intensityTotals.peakSeries))
    .map((week, it) => {
      return { x: it, y: _.max(_.map(week, "peak")) * 100 };
    })
    .flatten()
    .value();

  const averageSeries = _(_.compact(intensityTotals.averageSeries))
    .groupBy("week")
    .map((week, it) => {
      week.average = week.average * 100;
      return { x: it, y: (_.sum(_.map(week, "average")) / week.length) * 100 };
    })
    .flatten()
    .value();

  const minTick = 0; //Math.round(_.min(_.map(averageSeries, "y")) - 10)
  const maxTick = 100; //_.max(_.map(peakSeries, "y")) + 10;

  const selectedMetricsVolume =
    query.type === "total"
      ? exercisesVolumeForCycle
      : _.pick(exercisesVolumeForCycle, [query.type]);
  const selectedMetricsVolumeGroups = _.values(selectedMetricsVolume);

  // For a published cycle to be in My Programs, it has to be a duplicate
  const isMyProgram =
    program &&
    program.status !== "published" &&
    (cycle.status !== "published" ||
      String(cycle.source) !== String(cycle._id));
  let isPublished = false;
  if (cycle.programs) {
    isPublished = cycle.programs.every(
      (program) => program.status === "published"
    );
  }

  let avatar = _.get(locals, "activeUser.avatar.file");
  avatar = avatar
    ? `/api/file/${avatar}?w=90&h=90`
    : firstName && lastName
    ? generateAvatarSVG(firstName, lastName)
    : "/img/avatar-placeholder.webp";

  const addToCalendarPath = `/actions/refresh?modal[type]=add-to-calendar&cycle=${
    cycle._id
  }&program=${_.get(program, "order")}`;
  const suggestUpgradePath = `/actions/refresh?modal[type]=suggest-upgrade`;
  const isCoach = locals.activeUser.hasRole("coach");
  const userPlan =
    (!activeUser.organizationId && !organizationOwner) || !isCoach
      ? _.get(activeUser, "stripe.plan", "")
      : _.get(organizationOwner, "stripe.plan", "");
  const isPremium =
    userPlan === STRIPE.PLANS.V2.premiumMonthly ||
    userPlan === STRIPE.PLANS.V2.premiumAnnually;

  const isAdmin =
    _.get(activeUser, "admin", false) ||
    activeUser.permissions?.includes("admin");
  const isAmbassador = activeUser.permissions?.includes("ambassador");

  const previewPath = `/dashboard/program-library/program-preview/${
    cycle._id
  }?&program=${activeProgramIndex + 1}`;

  return (
    <main className="program-overview">
      <header>
        <hgroup>
          {locals.query.referrer === "program-builder" ||
          locals.query.referrer === undefined ? (
            <a href="/dashboard/program-builder">
              <button data-icon-only>
                <SvgIcon src={"/img/new-icons/back-button.svg"} />
              </button>
              <span className="back">{t("programOverview.myPrograms")}</span>
            </a>
          ) : (
            <a href="/dashboard/program-library">
              <button>
                <SvgIcon src={"/img/new-icons/back-button.svg"} />
              </button>
              <span className="back">{t("nav.programLibrary")}</span>
            </a>
          )}
        </hgroup>

        <div className="title">{cycle.name}</div>
        {Boolean(program) &&
          (activeUser.hasRole("coachCanSubmit") &&
          String(activeUser._id) ===
            String(_.get(cycle, "createdBy._id", cycle.createdBy)) ? (
            <span>
              {isPublished && (
                <label className="button" style={{ pointerEvents: "none" }}>
                  {t("programOverview.published")}
                </label>
              )}
              {!isPublished && (
                <a
                  href={`/actions/refresh?modal[type]=submit-for-review&program=${_.get(
                    program,
                    "order"
                  )}`}
                  role="button"
                  data-button-type="secondary"
                >
                  {t("programOverview.submitForReview")}
                </a>
              )}
            </span>
          ) : (
            <a
              href={
                cycle.type === "premium" &&
                !isPremium &&
                !isAdmin &&
                !isAmbassador
                  ? suggestUpgradePath
                  : addToCalendarPath
              }
              role="button"
              data-button-type="primary"
            >
              {t("programOverview.addToCalendar")}
            </a>
          ))}

        {isAdmin &&
          isPublished &&
          (cycle.type === "standard" || cycle.type === "premium") && (
            <a
              href={`/actions/cycle/unpublish/${cycle._id}`}
              className="button unpublish"
            >
              {t("programOverview.unpublish")}
            </a>
          )}

        {isMyProgram && !isPublished && isCoach && (
          <a
            href={`/actions/refresh?modal[type]=edit-cycle-settings&cycleId=${cycle._id}`}
            className="cycle-settings"
          >
            <img src="/img/icons/settings_white.svg" />
            {t("programOverview.cycleSettings")}
          </a>
        )}
        {isMyProgram && isCoach && (
          <a
            href={`/actions/refresh?modal[type]=program-settings&cycleId=${cycle._id}`}
            className="new-program"
          >
            <i className="icon">
              <img src="/img/icons/plus_white.svg" />
            </i>{" "}
            {t("programOverview.newProgram")}
          </a>
        )}
      </header>

      <section className="Main">
        <div className="flex gap-2 items-center">
          <a href={previewPath} className="clipped">
            {t("programOverview.preview")}
          </a>
          {program ? program.name : t("programOverview.noProgramsFound")}
          {Boolean(program) && (
            <div className="prev-next">
              <a
                href={`/actions/refresh/?program=${activeProgramIndex}`}
                className={`prev ${activeProgramIndex === 0 ? "disabled" : ""}`}
              >
                <SvgIcon src={"/img/new-icons/arrow-left.svg"} />
                <span className="current">{activeProgramIndex + 1}</span>
              </a>
              /
              <a
                href={`/actions/refresh/?program=${activeProgramIndex + 2}`}
                className={`next ${
                  activeProgramIndex === cycle.programs.length - 1
                    ? "disabled"
                    : ""
                }`}
              >
                <span className="total">{cycle.programs.length}</span>
                <SvgIcon src={"/img/new-icons/arrow-right.svg"} />
              </a>
            </div>
          )}
        </div>

        {Boolean(program) &&
          isMyProgram &&
          locals.query.referrer === "program-builder" && (
            <a
              href="/actions/refresh/?modal[type]=edit-program-settings"
              className="program-settings"
            >
              <img className="icon-yellow" src="/img/icons/settings.svg" />{" "}
              {t("planTile.settings")}
            </a>
          )}

        {Boolean(program) &&
          isMyProgram &&
          locals.query.referrer === "program-builder" && (
            <a
              href={`/dashboard/program-builder/edit-program/${
                cycle._id
              }?program=${program ? program.order : 0}`}
              className="program-edit"
            >
              <img src="/img/icons/edit.svg" /> {t("common:edit")}
            </a>
          )}

        {Boolean(program) && (
          <div>
            <div className="Program-Overview-Details">
              <div className="flex gap-2">
                <section role="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th colSpan={2}>{t("programOverview.numberOfLifts")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{t("volumeIntensity.total")}</td>
                        <td>{totalLiftSum}</td>
                      </tr>
                      <tr className="bench">
                        <td>{t("volumeIntensity.bench")}</td>
                        <td>{liftsTotals.bench || 0}</td>
                      </tr>
                      <tr className="deadlift">
                        <td>{t("volumeIntensity.deadlift")}</td>
                        <td>{liftsTotals.deadlift || 0}</td>
                      </tr>
                      <tr className="squat">
                        <td>{t("volumeIntensity.squat")}</td>
                        <td>{liftsTotals.squat || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </section>

                <section role="table-wrapper">
                  <table>
                    <thead className="left-side">
                      <tr>
                        <th colSpan={2}>{t("programOverview.volumeSplit")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{t("volumeIntensity.total")}</td>
                        <td>100%</td>
                      </tr>
                      <tr className="bench">
                        <td>{t("volumeIntensity.bench")}</td>
                        <td>
                          {Math.round(
                            (volumeTotalsByLift.bench / totalLiftVolume) * 100
                          ) || 0}
                          %
                        </td>
                      </tr>
                      <tr className="deadlift">
                        <td>{t("volumeIntensity.deadlift")}</td>
                        <td>
                          {Math.round(
                            (volumeTotalsByLift.deadlift / totalLiftVolume) *
                              100
                          ) || 0}
                          %
                        </td>
                      </tr>
                      <tr className="squat">
                        <td>{t("volumeIntensity.squat")}</td>
                        <td>
                          {Math.round(
                            (volumeTotalsByLift.squat / totalLiftVolume) * 100
                          ) || 0}
                          %
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </section>
                <section role="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th colSpan={2}>{t("volumeIntensity.intensity")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{t("programOverview.average")}</td>
                        <td>
                          {intensityTotals.average === 0
                            ? t("programOverview.na")
                            : `${_.round(intensityTotals.average, 1)}%`}
                        </td>
                      </tr>
                      <tr>
                        <td>{t("programOverview.peak")}</td>
                        <td>
                          {intensityTotals.average === 0
                            ? t("programOverview.na")
                            : `${_.round(intensityTotals.peak * 100, 1)}%`}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </section>
                <section role="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th colSpan={2}>{t("programOverview.workouts")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{t("volumeIntensity.total")}</td>
                        <td>{program ? program.sessions.length : 0}</td>
                      </tr>
                      <tr>
                        <td>{t("programOverview.perWeek")}</td>
                        <td>
                          {_(program.sessions)
                            .groupBy("week")
                            .map((w) => w.length)
                            .max()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </section>
              </div>
              <div className="program-details">
                {program && program.goals && (
                  <article>
                    <b>{t("programOverview.goals")}</b>
                    <div className="body">
                      <div className="chart">{program.goals}</div>
                    </div>
                  </article>
                )}
                {program && program.equipmentRequired && (
                  <article className="column" data-col="1/4">
                    <b>{t("programOverview.equipmentRequired")}</b>
                    <div className="body">
                      <div className="chart">{program.equipmentRequired}</div>
                    </div>
                  </article>
                )}
                <article className="column" data-col="1/2">
                  <b>{t("programOverview.programDescription")}</b>
                  <div className="body">
                    <div className="chart">
                      {program ? program.description : ""}
                    </div>
                  </div>
                </article>
              </div>
            </div>

            <div className="Program-Overview-Chart">
              <form id="metrics-form" action="#_" method="GET" data-noreset>
                <div className="Chart-Frame bar-chart">
                  <header>
                    <hgroup>
                      <ToggleLinksInput
                        className="tabs"
                        name="type"
                        options={[
                          { text: t("volumeIntensity.total"), value: "total" },
                          { text: t("volumeIntensity.squat"), value: "squat" },
                          { text: t("volumeIntensity.bench"), value: "bench" },
                          {
                            text: t("volumeIntensity.deadlift"),
                            value: "deadlift",
                          },
                        ]}
                      />
                    </hgroup>

                    <hgroup>
                      <ToggleLinksInput
                        name="units"
                        options={[
                          { text: t("baselineVolume.kg"), value: "metric" },
                          { text: t("baselineVolume.lbs"), value: "imperial" },
                        ]}
                      />
                    </hgroup>
                  </header>

                  <div className="body">
                    {!_.isEmpty(selectedMetricsVolume) && (
                      <div>
                        <div className="legend">
                          {query.type !== "total" && (
                            <div className={`bar series ${query.type}`}>
                              {t("volumeIntensity.volume")}
                            </div>
                          )}
                          {intensityTotals.average > 0 && (
                            <div className="line series-a">
                              {t("volumeIntensity.avgIntensity")}
                            </div>
                          )}
                          {intensityTotals.average > 0 && (
                            <div className="line series-b">
                              {t("volumeIntensity.peakIntensity")}
                            </div>
                          )}
                        </div>
                        <div className="axis-labels">
                          <div className="left">{t("numberOfLifts.lifts")}</div>
                          <div className="right">
                            {t("volumeIntensity.intensity")}
                          </div>
                        </div>
                        <div className="chart-fullsize">
                          <div className="labels">
                            <Chart
                              type="Bar"
                              only="label"
                              data={{ series: liftSeries }}
                              options={{
                                width: "60px",
                                height: "380px",
                                stackBars: true,
                                axisX: { showGrid: false, showLabel: false },
                                axisY: {
                                  showGrid: false,
                                  showLabel: true,
                                  onlyInteger: true,
                                  scaleMinSpace: 30,
                                },
                              }}
                            />

                            <Chart
                              type="Bar"
                              only="label"
                              data={{ series: [] }}
                              options={{
                                width: "60px",
                                height: "380px",
                                axisX: { showGrid: false, showLabel: false },
                                axisY: {
                                  type:
                                    process.browser &&
                                    require("chartist").StepAxis,
                                  stretch: true,
                                  showGrid: false,
                                  showLabel: true,
                                  ticks: _.range(minTick, maxTick, 10).map(
                                    (i) => `${i}%`
                                  ),
                                },
                              }}
                            />
                          </div>

                          <div className="data">
                            <Chart
                              type="Bar"
                              data={{
                                labels: labels,
                                series: liftSeries,
                              }}
                              options={{
                                width: `${_.size(keys) * 40}px`,
                                height: "380px",
                                stackBars: true,
                                chartPadding: { left: 35, right: 75 },
                                axisX: { showGrid: false, showLabel: true },
                                axisY: {
                                  showGrid: true,
                                  showLabel: false,
                                  scaleMinSpace: 30,
                                },
                              }}
                              tooltip={(item, i) => {
                                const key = keys[i];
                                return (
                                  <div
                                    className="tooltip"
                                    style={{ left: item.x }}
                                  >
                                    <div className="details">
                                      <div>
                                        <b>{t("programOverview.weekNumber", { key })}</b>
                                      </div>
                                      {_.compact(
                                        _.map(liftSeries, ({ data, name }) => {
                                          const totalLifts = data[i];
                                          if (!totalLifts) return;
                                          return (
                                            <div key={name}>
                                              {_.capitalize(name)}{" "}
                                              <b>{totalLifts}</b>
                                            </div>
                                          );
                                        })
                                      )}
                                      <div>
                                        {t("volumeIntensity.total")} <b>{totalVolumes[i]}</b>
                                      </div>
                                      {/*<div>Volume <b>{locals.withWeight(locals.fromMetric(accumulatedVolumes[i], units), units)}</b></div>*/}
                                    </div>
                                  </div>
                                );
                              }}
                            />

                            {intensityTotals.average > 0 &&
                              _.size(keys) >= 2 && (
                                <Chart
                                  type="Line"
                                  data={{ series: [averageSeries, peakSeries] }}
                                  options={{
                                    width: `${_.size(keys) * 40}px`,
                                    height: "380px",
                                    showPoint: false,
                                    chartPadding: { left: 35, right: 75 },
                                    axisX: {
                                      showGrid: false,
                                      showLabel: false,
                                    },
                                    axisY: {
                                      low: minTick,
                                      high: maxTick, // / 100,
                                      showGrid: false,
                                      showLabel: false,
                                    },
                                  }}
                                />
                              )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Hiding this for now - almost ready to display  */}
        {/* {cycle.programs.length > 1 && (
          <div className="Program-Overview-More">
            <h5>More Programs in {cycle.name}</h5>
            <div className="columns">
              {_.map(_.sortBy(cycle.programs, "order"), (program, index) => (
                <ProgramCard
                  program={program}
                  cycle={cycle}
                  cardIndex={index}
                  isOverview={true}
                  referrer="program-library"
                  activeProgramIndex={activeProgramIndex}
                />
              ))}
            </div>
          </div>
        )} */}
      </section>
    </main>
  );
}

function handleDayChange() {
  submit("#_", { body: document.getElementById("metrics-form") });
}

const ProgramOverview = (app) => {
  app
    .get(
      "/program-overview/:cycleId",
      async ({ res, req, locals, load, api }) => {
        const { query, params } = req;

        _.defaults(query, {
          program: "1",
          units: locals.units,
          period: "week",
          type: "total",
        });

        const modal = _.get(query, "modal", {});
        if (modal.type === "add-to-calendar") {
          await load("activeExercises", {
            start: moment(-1),
            end: moment("December 30 2049"),
          });
        }

        await Promise.all([
          load("editCycle", { cycle: params.cycleId }),
          load("libraryCycles"),
          load("exercisesVolumeForCycle", {
            id: params.cycleId,
            program: query.program,
          }),
          load("exercisesLiftsForCycle", {
            id: params.cycleId,
            program: query.program,
          }),
          load("exercisesVolumeGroupZonesForCycle", {
            id: params.cycleId,
            program: query.program,
          }),
        ]);
        locals.activeProgramIndex = req.query.program - 1 || 0;
        // if (locals.activeProgramIndex > locals.editCycle.programs.length - 1 || locals.ActiveProgramIndex < 0) locals.activeProgramIndex = 0;
        const cycle = locals.editCycle
          ? locals.editCycle
          : _.find(locals.libraryCycles, { _id: locals.params.cycleId });
        locals.modal = {
          "add-to-calendar": <AddToCalendarModal cycle={cycle} />,
          "edit-program-settings": <EditProgramSettingsModal />,
          "submit-for-review": <SubmitForReviewModal />,
          "edit-cycle-settings": <EditCycleSettingsModal />,
          "program-settings": <ProgramSettingsModal />,
          "suggest-upgrade": <SuggestUpgradeModal api={api} />,
        }[_.get(locals.query, "modal.type")];

        res.body = <View />;
      }
    );
};

export default ProgramOverview;
